import {
    type CreateFeedingInput,
    type CreateNappyInput,
    type CreatePumpingInput,
    type CreateSleepInput,
    type ActivityType,
    type DayRollup,
    type DaySummary,
    dayRollupListSchema,
    daySummarySchema,
    daysPath,
    type Feeding,
    feedingSchema,
    feedingsPath,
    feedingPath,
    feedingSidePath,
    feedingStopPath,
    type NappyChange,
    nappiesPath,
    nappyPath,
    nappySchema,
    type PumpingSession,
    pumpingPath,
    pumpingSchema,
    pumpingSessionPath,
    pumpingStopPath,
    type SleepSession,
    sleepPath,
    sleepSchema,
    sleepSessionPath,
    sleepStopPath,
    type StopPumpingInput,
    type StopTimerInput,
    type SwitchSideInput,
    summaryPath,
    type TimelineItem,
    timelinePath,
    timelineSchema,
    type UpdateFeedingInput,
    type UpdateNappyInput,
    type UpdatePumpingInput,
    type UpdateSleepInput,
} from '@baby-tracker/contracts';
import {
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from '@tanstack/react-query';

import { apiGet, apiRequest } from '../lib/api-client.js';
import { queryKeys } from '../lib/query-keys.js';

function useInvalidateTracking(familyId: string): () => Promise<void> {
    const queryClient = useQueryClient();
    return async () => {
        await queryClient.invalidateQueries({ queryKey: ['summary', familyId] });
        await queryClient.invalidateQueries({ queryKey: ['days', familyId] });
        await queryClient.invalidateQueries({ queryKey: ['timeline', familyId] });
    };
}

export function useDaySummary(
    familyId: string | undefined,
    date: string | undefined,
): UseQueryResult<DaySummary> {
    return useQuery({
        queryKey: queryKeys.summary(familyId ?? '', date ?? ''),
        enabled: familyId !== undefined && date !== undefined,
        queryFn: ({ signal }) =>
            apiGet(`${summaryPath(familyId ?? '')}?date=${date ?? ''}`, daySummarySchema, {
                signal,
            }),
    });
}

export function useDayRollups(
    familyId: string | undefined,
    from: string | undefined,
    to: string | undefined,
): UseQueryResult<DayRollup[]> {
    return useQuery({
        queryKey: queryKeys.days(familyId ?? '', from ?? '', to ?? ''),
        enabled: familyId !== undefined && from !== undefined && to !== undefined,
        queryFn: ({ signal }) =>
            apiGet(
                `${daysPath(familyId ?? '')}?from=${from ?? ''}&to=${to ?? ''}`,
                dayRollupListSchema,
                {
                    signal,
                },
            ),
    });
}

export function useTimeline(
    familyId: string | undefined,
    from: string | undefined,
    to: string | undefined,
    filter?: { childId?: string; type?: ActivityType },
): UseQueryResult<TimelineItem[]> {
    const childId = filter?.childId ?? '';
    const type = filter?.type ?? '';
    const params = new URLSearchParams();
    if (from !== undefined) params.set('from', from);
    if (to !== undefined) params.set('to', to);
    if (filter?.childId !== undefined) params.set('childId', filter.childId);
    if (filter?.type !== undefined) params.set('type', filter.type);
    return useQuery({
        queryKey: queryKeys.timeline(familyId ?? '', from ?? '', to ?? '', childId, type),
        enabled: familyId !== undefined && from !== undefined && to !== undefined,
        queryFn: ({ signal }) =>
            apiGet(`${timelinePath(familyId ?? '')}?${params.toString()}`, timelineSchema, {
                signal,
            }),
    });
}

export function useCreateFeeding(
    familyId: string,
): UseMutationResult<Feeding, Error, CreateFeedingInput> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: CreateFeedingInput) =>
            apiRequest('POST', feedingsPath(familyId), feedingSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useUpdateFeeding(
    familyId: string,
): UseMutationResult<Feeding, Error, { id: string; body: UpdateFeedingInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: UpdateFeedingInput }) =>
            apiRequest('PATCH', feedingPath(input.id), feedingSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useDeleteFeeding(familyId: string): UseMutationResult<Feeding, Error, string> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (id: string) => apiRequest('DELETE', feedingPath(id), feedingSchema),
        onSuccess: invalidate,
    });
}

export function useStopFeeding(
    familyId: string,
): UseMutationResult<Feeding, Error, { id: string; body: StopTimerInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: StopTimerInput }) =>
            apiRequest('POST', feedingStopPath(input.id), feedingSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useSwitchFeedingSide(
    familyId: string,
): UseMutationResult<Feeding, Error, { id: string; body: SwitchSideInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: SwitchSideInput }) =>
            apiRequest('POST', feedingSidePath(input.id), feedingSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useCreatePumping(
    familyId: string,
): UseMutationResult<PumpingSession, Error, CreatePumpingInput> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: CreatePumpingInput) =>
            apiRequest('POST', pumpingPath(familyId), pumpingSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useUpdatePumping(
    familyId: string,
): UseMutationResult<PumpingSession, Error, { id: string; body: UpdatePumpingInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: UpdatePumpingInput }) =>
            apiRequest('PATCH', pumpingSessionPath(input.id), pumpingSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useDeletePumping(
    familyId: string,
): UseMutationResult<PumpingSession, Error, string> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (id: string) => apiRequest('DELETE', pumpingSessionPath(id), pumpingSchema),
        onSuccess: invalidate,
    });
}

export function useStopPumping(
    familyId: string,
): UseMutationResult<PumpingSession, Error, { id: string; body: StopPumpingInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: StopPumpingInput }) =>
            apiRequest('POST', pumpingStopPath(input.id), pumpingSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useCreateSleep(
    familyId: string,
): UseMutationResult<SleepSession, Error, CreateSleepInput> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: CreateSleepInput) =>
            apiRequest('POST', sleepPath(familyId), sleepSchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useUpdateSleep(
    familyId: string,
): UseMutationResult<SleepSession, Error, { id: string; body: UpdateSleepInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: UpdateSleepInput }) =>
            apiRequest('PATCH', sleepSessionPath(input.id), sleepSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useDeleteSleep(familyId: string): UseMutationResult<SleepSession, Error, string> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (id: string) => apiRequest('DELETE', sleepSessionPath(id), sleepSchema),
        onSuccess: invalidate,
    });
}

export function useStopSleep(
    familyId: string,
): UseMutationResult<SleepSession, Error, { id: string; body: StopTimerInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: StopTimerInput }) =>
            apiRequest('POST', sleepStopPath(input.id), sleepSchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useCreateNappy(
    familyId: string,
): UseMutationResult<NappyChange, Error, CreateNappyInput> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: CreateNappyInput) =>
            apiRequest('POST', nappiesPath(familyId), nappySchema, { body: input }),
        onSuccess: invalidate,
    });
}

export function useUpdateNappy(
    familyId: string,
): UseMutationResult<NappyChange, Error, { id: string; body: UpdateNappyInput }> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (input: { id: string; body: UpdateNappyInput }) =>
            apiRequest('PATCH', nappyPath(input.id), nappySchema, { body: input.body }),
        onSuccess: invalidate,
    });
}

export function useDeleteNappy(familyId: string): UseMutationResult<NappyChange, Error, string> {
    const invalidate = useInvalidateTracking(familyId);
    return useMutation({
        mutationFn: (id: string) => apiRequest('DELETE', nappyPath(id), nappySchema),
        onSuccess: invalidate,
    });
}
